import { useCallback, useEffect, useMemo, useState } from "react";
import { toast } from "sonner";
import api from "../../lib/api";

const TYPES = [
  "article", "symptom", "condition", "comparison", "faq",
  "provider", "state", "city", "research", "legal",
];

const STATUS_STYLES = {
  published: "bg-green-50 text-green-800 border-green-200",
  in_review: "bg-amber-50 text-amber-900 border-amber-200",
  draft:     "bg-stone-100 text-stone-700 border-stone-200",
  archived:  "bg-rose-50 text-rose-700 border-rose-200",
};

/**
 * Admin CMS dashboard. Groups every content_pages row by page_type, exposes
 * quality score, editorial status, AI-generated badge, and Approve/Unpublish
 * actions for reviewers.
 *
 * Editorial workflow rules (enforced server-side, mirrored here):
 *   - Editors save drafts. POSTs with status='published' from editors are
 *     silently demoted to 'in_review' by the API.
 *   - Only medical_reviewer / compliance_reviewer / super_admin can approve.
 *   - Approvals record `approved_by` + `approved_at`; every write appends
 *     to `revision_history`.
 */
export function ContentDashboard() {
  const [rows, setRows] = useState([]);
  const [filter, setFilter] = useState("");
  const [statusFilter, setStatusFilter] = useState("all");
  const [showAIOnly, setShowAIOnly] = useState(false);

  const load = useCallback(async () => {
    try {
      const { data } = await api.get("/admin/content");
      setRows(data);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to load content");
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const filtered = useMemo(() => rows.filter((r) => {
    if (statusFilter !== "all" && (r.status || (r.public_visibility ? "published" : "draft")) !== statusFilter) return false;
    if (showAIOnly && !r.ai_generated) return false;
    if (!filter) return true;
    const q = filter.toLowerCase();
    return r.slug.includes(q) || (r.title || "").toLowerCase().includes(q);
  }), [rows, filter, statusFilter, showAIOnly]);

  const byType = TYPES.map((t) => ({
    type: t,
    items: filtered.filter((r) => r.page_type === t),
  }));

  const scoreClass = (s) => s >= 90 ? "bg-green-50 text-green-800 border-green-200"
                        : s >= 70 ? "bg-amber-50 text-amber-800 border-amber-200"
                        : "bg-rose-50 text-rose-800 border-rose-200";

  const overall = useMemo(() => {
    if (!rows.length) return 0;
    return Math.round(rows.reduce((n, r) => n + (r.quality?.score || 0), 0) / rows.length);
  }, [rows]);

  const statusCounts = useMemo(() => {
    const c = { published: 0, in_review: 0, draft: 0, archived: 0, ai_drafts: 0 };
    rows.forEach((r) => {
      const s = r.status || (r.public_visibility ? "published" : "draft");
      if (c[s] !== undefined) c[s] += 1;
      if (r.ai_generated) c.ai_drafts += 1;
    });
    return c;
  }, [rows]);

  const doApprove = useCallback(async (r) => {
    try {
      await api.post(`/admin/content/${r.page_type}/${r.slug}/approve`);
      toast.success(`Published: ${r.title}`);
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Approval failed");
    }
  }, [load]);

  const doUnpublish = useCallback(async (r) => {
    try {
      await api.post(`/admin/content/${r.page_type}/${r.slug}/unpublish`);
      toast.success(`Unpublished: ${r.title}`);
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Unpublish failed");
    }
  }, [load]);

  return (
    <div data-testid="content-dashboard" className="space-y-6">
      <div className="vv-card p-6 grid md:grid-cols-[1fr_1fr_1fr] gap-4">
        <div>
          <div className="text-xs uppercase tracking-widest text-stone-500">Content quality</div>
          <div className="font-display text-3xl mt-1">{overall}<span className="text-lg text-stone-400">/100</span></div>
          <div className="text-xs text-stone-500 mt-1">Average editorial score across {rows.length} CMS pages</div>
        </div>
        <div className="flex flex-wrap gap-2 items-center text-xs">
          <StatusPill label="Published" count={statusCounts.published} style={STATUS_STYLES.published} />
          <StatusPill label="In review" count={statusCounts.in_review} style={STATUS_STYLES.in_review} />
          <StatusPill label="Draft" count={statusCounts.draft} style={STATUS_STYLES.draft} />
          {statusCounts.ai_drafts ? <StatusPill label="AI drafts" count={statusCounts.ai_drafts} style="bg-indigo-50 text-indigo-800 border-indigo-200" /> : null}
        </div>
        <div className="flex flex-col gap-2">
          <input
            data-testid="content-filter"
            placeholder="Filter by title or slug…"
            value={filter}
            onChange={(e) => setFilter(e.target.value)}
            className="bg-white border border-stone-200 rounded-full px-4 py-2 text-sm outline-none focus:border-[color:var(--vv-teal)]"
          />
          <div className="flex gap-2 items-center flex-wrap">
            <select
              data-testid="content-status-filter"
              value={statusFilter}
              onChange={(e) => setStatusFilter(e.target.value)}
              className="text-xs border border-stone-200 rounded-full px-3 py-1.5"
            >
              <option value="all">All statuses</option>
              <option value="published">Published</option>
              <option value="in_review">In review</option>
              <option value="draft">Draft</option>
              <option value="archived">Archived</option>
            </select>
            <label className="text-xs flex items-center gap-1.5 cursor-pointer">
              <input
                type="checkbox"
                data-testid="ai-only-toggle"
                checked={showAIOnly}
                onChange={(e) => setShowAIOnly(e.target.checked)}
              />
              AI drafts only
            </label>
          </div>
        </div>
      </div>

      {byType.map(({ type, items }) => (
        <div key={type} data-testid={`content-group-${type}`}>
          <div className="text-xs uppercase tracking-widest text-stone-500 mb-2">{type} ({items.length})</div>
          {items.length === 0 ? (
            <div className="vv-card p-4 text-sm text-stone-500">No {type} pages yet.</div>
          ) : (
            <div className="vv-card overflow-hidden">
              <table className="w-full text-sm">
                <thead className="bg-stone-50 border-b border-stone-200">
                  <tr>
                    {["Title", "Slug", "Status", "Reviewer", "Reviewed", "Score", "Gaps", ""].map((h, i) => (
                      <th key={`h-${type}-${i}`} className="text-left px-4 py-2 text-xs uppercase tracking-widest text-stone-500">{h}</th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {items.map((r) => {
                    const gaps = [...(r.quality?.missing_required || []), ...(r.quality?.bonus_gaps || [])];
                    const status = r.status || (r.public_visibility ? "published" : "draft");
                    return (
                      <tr key={`${r.page_type}-${r.slug}`} className="border-b border-stone-100">
                        <td className="px-4 py-2 font-medium">
                          <div className="flex items-center gap-2 flex-wrap">
                            <span>{r.title}</span>
                            {r.ai_generated ? (
                              <span data-testid="ai-generated-badge" className="text-[10px] uppercase tracking-widest bg-indigo-50 text-indigo-800 border border-indigo-200 rounded-full px-2 py-0.5">
                                AI · review required
                              </span>
                            ) : null}
                          </div>
                        </td>
                        <td className="px-4 py-2 text-xs text-stone-500">{r.parent_slug ? `${r.parent_slug}/` : ""}{r.slug}</td>
                        <td className="px-4 py-2">
                          <span className={`inline-block text-[11px] font-semibold px-2 py-0.5 rounded-full border ${STATUS_STYLES[status] || STATUS_STYLES.draft}`}>
                            {status.replace("_", " ")}
                          </span>
                        </td>
                        <td className="px-4 py-2 text-xs">{r.medical_reviewer_name || <span className="text-rose-500">missing</span>}</td>
                        <td className="px-4 py-2 text-xs">{r.last_reviewed_on ? r.last_reviewed_on.slice(0, 10) : <span className="text-rose-500">—</span>}</td>
                        <td className="px-4 py-2">
                          <span className={`inline-block text-xs font-semibold px-2 py-0.5 rounded-full border ${scoreClass(r.quality?.score || 0)}`}>
                            {r.quality?.score || 0}
                          </span>
                        </td>
                        <td className="px-4 py-2 text-xs text-stone-600 max-w-[220px] truncate" title={gaps.join(", ")}>
                          {gaps.length === 0 ? <span className="text-green-700">complete</span> : gaps.slice(0, 3).join(", ")}
                        </td>
                        <td className="px-4 py-2 text-right whitespace-nowrap">
                          {status === "published" ? (
                            <button
                              data-testid={`unpublish-${r.page_type}-${r.slug}`}
                              onClick={() => doUnpublish(r)}
                              className="text-xs text-stone-600 hover:text-rose-700"
                            >
                              Unpublish
                            </button>
                          ) : (
                            <button
                              data-testid={`approve-${r.page_type}-${r.slug}`}
                              onClick={() => doApprove(r)}
                              className="text-xs text-white bg-[color:var(--vv-gold-deep)] hover:opacity-90 rounded-full px-3 py-1"
                            >
                              Approve & Publish
                            </button>
                          )}
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}
        </div>
      ))}
      <div className="text-xs text-stone-500">
        Full edit exposed via <code>POST /api/admin/content</code>. Approvals and unpublish call{" "}
        <code>/admin/content/{"{type}"}/{"{slug}"}/approve</code> and{" "}
        <code>/admin/content/{"{type}"}/{"{slug}"}/unpublish</code>.
      </div>
    </div>
  );
}

function StatusPill({ label, count, style }) {
  return (
    <span className={`inline-flex items-center gap-1.5 text-[11px] font-semibold px-2.5 py-1 rounded-full border ${style}`}>
      <span>{label}</span>
      <span className="opacity-70">{count}</span>
    </span>
  );
}
